import { HttpClient, HttpContext, HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { catchError, defer, finalize, Observable, throwError } from 'rxjs';

import { environment } from '../../environments/environment';
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);
import { LoginApiResponse } from '../models/login-response';
import { SpinnerService } from './spinner.service';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
  otp: string;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class SigninService {
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(StorageService);
  private readonly spinnerService = inject(SpinnerService);

  private readonly documentRef = inject(DOCUMENT);
  private readonly router = inject(Router);

  private readonly signinApiUrl = `${environment.API_URL}/signin`;

  private readonly userApiUrl = `${environment.API_URL}/user`;

  public doLogin(credentials: LoginRequest): Observable<LoginApiResponse> {
    return this.withSpinner(
      this.http.post<LoginApiResponse>(`${environment.API_URL}/signin`, credentials).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.spinnerService.showMessage('Bad credentials', 'error');
          }

          return throwError(() => error);
        }),
      ),
    );
  }

  public async doLogout(): Promise<void> {
    const browserWindow = this.documentRef.defaultView;

    browserWindow?.sessionStorage.clear();

    browserWindow?.localStorage.removeItem('token');
    browserWindow?.localStorage.removeItem('identity');
    browserWindow?.localStorage.removeItem('user');

    await this.router.navigateByUrl('/signin', {
      replaceUrl: true,
    });
  }

  public checkPassword(password: string): Observable<boolean> {
    return this.withSpinner(
      this.http.post<boolean>(`${this.userApiUrl}/checkPassword`, {
        oldPassword: password,
      }),
    );
  }

  public sendOtp(email: string): Observable<unknown> {
    return this.withSpinner(
      this.http.post<unknown>(
        `${this.signinApiUrl}/send_otp`,
        {
          email: email.trim(),
        },
        {
          context: this.createPublicContext(),
        },
      ),
    );
  }

  public updatePassword(data: ForgotPasswordRequest): Observable<unknown> {
    return this.withSpinner(
      this.http.put<unknown>(`${this.signinApiUrl}/forgot_password`, data, {
        context: this.createPublicContext(),
      }),
    );
  }

  private createPublicContext(): HttpContext {
    return new HttpContext().set(SKIP_AUTH, true);
  }

  private withSpinner<T>(request$: Observable<T>): Observable<T> {
    return defer(() => {
      this.spinnerService.show();

      return request$.pipe(
        finalize(() => {
          this.spinnerService.hide();
        }),
      );
    });
  }

  private handleLoginError(error: HttpErrorResponse): Observable<never> {
    if (error.status === 401) {
      this.spinnerService.showMessage('Bad credentials', 'error');
    }

    return throwError(() => error);
  }
}
